'use client';

import UiIcon from '@/components/ui/UiIcon';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import SiteHeader from '@/components/SiteHeader';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { IconUser } from '@/components/ui/MonoIcons';
import { Project, ProjectInvite, ProjectJoinRequestMyRow, ProjectJoinRequestOwnerRow, apiClient, Organization } from '@/lib/api';
import { getAccessToken } from '@/lib/authStorage';
import OrganizationMap from '@/components/projects/OrganizationMap';
import OrganizationSelect from '@/components/OrganizationSelect';
import ProjectDirectionPicker from '@/components/projects/ProjectDirectionPicker';
import ProjectPullRefresh from '@/components/projects/ProjectPullRefresh';
import type { ProjectDirection } from '@/lib/projectDirections';
import { getProjectOrganizationIds, homeProjectScopes, parseProjectOrganization } from '@/lib/homeProjectScopes';

// Режимы отображения страницы
type ViewMode = 'list' | 'map';

type ProjectsTabId = 'all' | 'my' | 'lab' | 'community' | 'invites' | 'requests';

const PROJECT_TABS: Array<{ id: ProjectsTabId; label: string; icon: string; authOnly?: boolean }> = [
  { id: 'all', label: 'Все', icon: 'ClipboardText' },
  { id: 'my', label: 'Мои', icon: 'Star', authOnly: true },
  { id: 'lab', label: 'Лаборатория', icon: 'Buildings' },
  { id: 'community', label: 'Пользователи', icon: 'Users' },
  { id: 'invites', label: 'Приглашения', icon: 'Envelope', authOnly: true },
  { id: 'requests', label: 'Заявки', icon: 'ClipboardText', authOnly: true },
];

export default function ProjectsPage() {
  return <Suspense fallback={<div role="status" className="p-6 text-site-secondary">Загружаем проекты…</div>}><ProjectsContent /></Suspense>;
}

function ProjectsContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const refreshingRef = useRef(false);
  const organizationId = parseProjectOrganization(searchParams.get('organization'));
  const [organizationCatalog, setOrganizationCatalog] = useState<{ data: Organization[]; error?: string } | null>(null);
  const scopedOrganization = useMemo(() => {
    if (organizationId === null || !organizationCatalog) return null;
    const organization = organizationCatalog.data.find(item => Number(item.id) === organizationId);
    return { ids: getProjectOrganizationIds(organizationCatalog.data, organizationId),
      name: organization?.short_name || organization?.name || '',
      error: organizationCatalog.error || (!organization ? 'Организация не найдена' : undefined) };
  }, [organizationId, organizationCatalog]);
  const scopeName = scopedOrganization?.name || homeProjectScopes.find(item => item.id === organizationId)?.name;
  const organizationGroups = useMemo(() => {
    const groups = new Map<string, { label: string; items: Organization[] }>();
    for (const item of organizationCatalog?.data || []) {
      if (!groups.has(item.type)) groups.set(item.type, { label: item.type_label || item.type, items: [] });
      groups.get(item.type)!.items.push(item);
    }
    return Array.from(groups.values());
  }, [organizationCatalog]);
  const selectOrganization = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set('organization', value); else params.delete('organization');
    router.push(`/projects${params.size ? `?${params}` : ''}`, { scroll: false });
  };
  // Режим отображения
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  
  const [my, setMy] = useState<Project[]>([]);
  const [publicProjects, setPublicProjects] = useState<Project[]>([]);
  const [publicUserProjects, setPublicUserProjects] = useState<Project[]>([]);
  const [invites, setInvites] = useState<ProjectInvite[]>([]);
  const [myJoinRequests, setMyJoinRequests] = useState<ProjectJoinRequestMyRow[]>([]);
  const [joinRequests, setJoinRequests] = useState<ProjectJoinRequestOwnerRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isAuthed, setIsAuthed] = useState<boolean>(false);
  const [joinRequestsActing, setJoinRequestsActing] = useState<number | null>(null);

  const [createForm, setCreateForm] = useState<{ name: string; description: string; directions: ProjectDirection[]; is_public: boolean; organization_id: number | null }>({
    name: '',
    description: '',
    directions: [],
    is_public: true,
    organization_id: null,
  });
  const [creating, setCreating] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<ProjectsTabId>('all');
  const [query, setQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Все проекты для карты (публичные + мои, чтобы видеть свои private тоже)
  const allProjects = useMemo(() => {
    const combined = isAuthed ? [...publicProjects, ...publicUserProjects, ...my] : [...publicProjects, ...publicUserProjects];
    const seen = new Set<number>();
    return combined.filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    }).sort((a, b) => {
      const aRank = a.display_rank ?? Number.MAX_SAFE_INTEGER;
      const bRank = b.display_rank ?? Number.MAX_SAFE_INTEGER;
      return aRank - bRank;
    });
  }, [publicProjects, publicUserProjects, my, isAuthed]);

  const refresh = async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setLoading(true);
    try {
      const token = getAccessToken();
      const authed = Boolean(token);
      setIsAuthed(authed);

      const [labRes, userRes, myRes, invRes, myJrRes, jrRes, orgRes] = await Promise.allSettled([
        apiClient.getPublicProjects(24),
        apiClient.getPublicUserProjects(24),
        authed ? apiClient.getMyProjects() : Promise.resolve([] as Project[]),
        authed ? apiClient.getMyProjectInvites() : Promise.resolve([] as ProjectInvite[]),
        authed ? apiClient.getMyProjectJoinRequests({ status: 'all' }) : Promise.resolve([] as ProjectJoinRequestMyRow[]),
        authed ? apiClient.getProjectJoinRequests({ status: 'pending' }) : Promise.resolve([] as ProjectJoinRequestOwnerRow[]),
        apiClient.getOrganizations({ flat: true, active_only: true }),
      ]);

      setOrganizationCatalog(orgRes.status === 'fulfilled' && orgRes.value.success ? { data: orgRes.value.data }
        : { data: [], error: 'Не удалось загрузить организации. Обновите каталог или откройте все проекты.' });
      if (labRes.status === 'fulfilled' && userRes.status === 'fulfilled') setMessage(previous => previous?.type === 'error' ? null : previous);

      if (labRes.status === 'fulfilled') {
        setPublicProjects(labRes.value);
      } else {
        setPublicProjects([]);
        const msg = (labRes as PromiseRejectedResult).reason?.message || 'Ошибка загрузки проектов лаборатории';
        setMessage({ type: 'error', text: msg });
      }

      if (userRes.status === 'fulfilled') {
        setPublicUserProjects(userRes.value);
      } else {
        setPublicUserProjects([]);
        const msg = (userRes as PromiseRejectedResult).reason?.message || 'Ошибка загрузки проектов пользователей';
        setMessage({ type: 'error', text: msg });
      }

      setMy(myRes.status === 'fulfilled' ? myRes.value : []);
      setInvites(invRes.status === 'fulfilled' ? invRes.value : []);
      setMyJoinRequests(myJrRes.status === 'fulfilled' ? myJrRes.value : []);
      setJoinRequests(jrRes.status === 'fulfilled' ? jrRes.value : []);
    } catch (error: any) {
      console.error('Ошибка загрузки проектов:', error);
      setMessage({ type: 'error', text: error.message || 'Ошибка загрузки проектов' });
    } finally {
      refreshingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (getAccessToken()) setActiveTab((prev) => (prev === 'all' ? 'all' : prev));
  }, []);

  useEffect(() => {
    if (isAuthed) return;
    if (activeTab === 'my' || activeTab === 'invites' || activeTab === 'requests') {
      setActiveTab('all');
    }
  }, [activeTab, isAuthed]);

  const myIds = useMemo(() => new Set(my.map((p) => p.id)), [my]);
  const myJoinByProject = useMemo(() => {
    const m = new Map<number, ProjectJoinRequestMyRow>();
    for (const r of myJoinRequests || []) m.set(r.project_id, r);
    return m;
  }, [myJoinRequests]);

  const queryNormalized = query.trim().toLowerCase();
  const projectMatches = useCallback((p: Project) => {
    if (organizationId !== null && !scopedOrganization?.ids.has(Number(p.organization_id))) return false;
    if (!queryNormalized) return true;
    const hay = [p.name, p.description, p.keywords, p.scientific_field, p.owner_display, p.owner_username]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(queryNormalized);
  }, [queryNormalized, organizationId, scopedOrganization]);

  const filteredMy = useMemo(() => my.filter(projectMatches), [my, projectMatches]);
  const filteredPublic = useMemo(() => publicProjects.filter(projectMatches), [publicProjects, projectMatches]);
  const filteredPublicUsers = useMemo(() => publicUserProjects.filter(projectMatches), [publicUserProjects, projectMatches]);
  const filteredAll = useMemo(() => allProjects.filter(projectMatches), [allProjects, projectMatches]);
  const filteredInvites = useMemo(() => {
    if (!queryNormalized) return invites;
    return invites.filter((inv) =>
      [inv.project_name, inv.invited_by_display, inv.invited_by_username, inv.message]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(queryNormalized)
    );
  }, [invites, queryNormalized]);
  const filteredJoinRequests = useMemo(() => {
    if (!queryNormalized) return joinRequests;
    return joinRequests.filter((r) =>
      [r.project_name, (r as any)?.sender?.display_name, (r as any)?.sender?.username, r.message]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(queryNormalized)
    );
  }, [joinRequests, queryNormalized]);

  const tabCounts = useMemo(() => ({
    all: filteredAll.length,
    my: filteredMy.length,
    lab: filteredPublic.length,
    community: filteredPublicUsers.length,
    invites: filteredInvites.length,
    requests: filteredJoinRequests.length,
  }), [filteredAll.length, filteredMy.length, filteredPublic.length, filteredPublicUsers.length, filteredInvites.length, filteredJoinRequests.length]);

  const showAll = activeTab === 'all';
  const showMy = activeTab === 'my';
  const showLab = activeTab === 'lab';
  const showCommunity = activeTab === 'community';
  const showInvitesSection = isAuthed && activeTab === 'invites';
  const showRequestsSection = isAuthed && activeTab === 'requests';
  const showProjectsGrid = showAll || (isAuthed && showMy) || showLab || showCommunity;

  const createProject = async () => {
    const name = createForm.name.trim();
    if (!name) {
      setMessage({ type: 'error', text: 'Введите название проекта' });
      return;
    }
    if (!createForm.organization_id) {
      setMessage({ type: 'error', text: 'Выберите организацию (лабораторию, кафедру и т.д.)' });
      return;
    }
    setCreating(true);
    setMessage(null);
    try {
      await apiClient.createProject({ 
        name, 
        description: createForm.description.trim(), 
        is_public: createForm.is_public,
        organization_id: createForm.organization_id,
        directions: createForm.directions,
      });
      setCreateForm({ name: '', description: '', directions: [], is_public: true, organization_id: null });
      setMessage({ type: 'success', text: 'Проект создан!' });
      await refresh();
      setTimeout(() => setMessage(null), 1500);
    } catch (error: any) {
      console.error('Ошибка создания проекта:', error);
      setMessage({ type: 'error', text: error.message || 'Ошибка создания проекта' });
    } finally {
      setCreating(false);
    }
  };

  const join = async (projectId: number) => {
    setMessage(null);
    try {
      if (!getAccessToken()) { window.location.href = '/login'; return; }
      const res = await apiClient.joinProject(projectId);
      const st = (res as any)?.status;
      if (st === 'already_member') setMessage({ type: 'success', text: 'Вы уже участник проекта' });
      else if (st === 'pending') setMessage({ type: 'success', text: 'Заявка отправлена владельцу проекта' });
      else setMessage({ type: 'success', text: 'Заявка отправлена' });
      await refresh();
      setTimeout(() => setMessage(null), 1200);
    } catch (error: any) {
      console.error('Ошибка вступления:', error);
      setMessage({ type: 'error', text: error.message || 'Ошибка вступления' });
    }
  };

  const leave = async (projectId: number) => {
    setMessage(null);
    try {
      await apiClient.leaveProject(projectId);
      setMessage({ type: 'success', text: 'Вы вышли из проекта' });
      await refresh();
      setTimeout(() => setMessage(null), 1200);
    } catch (error: any) {
      console.error('Ошибка выхода:', error);
      setMessage({ type: 'error', text: error.message || 'Ошибка выхода' });
    }
  };

  const acceptInvite = async (inviteId: number) => {
    setMessage(null);
    try {
      await apiClient.acceptProjectInvite(inviteId);
      setMessage({ type: 'success', text: 'Приглашение принято' });
      await refresh();
      setTimeout(() => setMessage(null), 1200);
    } catch (error: any) {
      console.error('Ошибка принятия приглашения:', error);
      setMessage({ type: 'error', text: error.message || 'Ошибка принятия приглашения' });
    }
  };

  const declineInvite = async (inviteId: number) => {
    setMessage(null);
    try {
      await apiClient.declineProjectInvite(inviteId);
      setMessage({ type: 'success', text: 'Приглашение отклонено' });
      await refresh();
      setTimeout(() => setMessage(null), 1200);
    } catch (error: any) {
      console.error('Ошибка отклонения приглашения:', error);
      setMessage({ type: 'error', text: error.message || 'Ошибка отклонения приглашения' });
    }
  };

  const statusLabel = (s?: string) => {
    switch (s) {
      case 'idea': return 'Идея';
      case 'active': return 'Активен';
      case 'paused': return 'Пауза';
      case 'completed': return 'Завершён';
      case 'archived': return 'Архив';
      default: return s || '—';
    }
  };

  const statusVariant = (s?: string) => {
    switch (s) {
      case 'active': return 'success' as const;
      case 'paused': return 'warning' as const;
      case 'completed': return 'success' as const;
      case 'archived':
      case 'idea':
      default: return 'secondary' as const;
    }
  };

  // Переключатель режимов отображения
  const ViewModeToggle = () => (
    <div className="flex shrink-0 items-center gap-0.5 p-0.5 rounded-xl bg-site-surface/60 border border-site-border">
      <button
        onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'} aria-label="Список" title="Список"
        className={`flex items-center justify-center gap-2 h-10 w-10 sm:w-auto sm:px-3 rounded-lg text-sm font-medium transition-colors ${
          viewMode === 'list'
            ? 'bg-site-accent text-site-on-accent shadow-lg'
            : 'text-site-muted hover:text-site-text hover:bg-site-raised/50'
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
        <span className="hidden sm:inline">Список</span>
      </button>
      <button
        onClick={() => setViewMode('map')} aria-pressed={viewMode === 'map'} aria-label="Карта" title="Карта"
        className={`flex items-center justify-center gap-2 h-10 w-10 sm:w-auto sm:px-3 rounded-lg text-sm font-medium transition-colors ${
          viewMode === 'map'
            ? 'bg-site-accent text-site-on-accent shadow-lg'
            : 'text-site-muted hover:text-site-text hover:bg-site-raised/50'
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        <span className="hidden sm:inline">Карта</span>
      </button>
    </div>
  );

  return (
    <ProjectPullRefresh refreshing={loading} onRefresh={refresh}>
      <SiteHeader homeAuth compact showProjectModeSwitch={false} hideFloatingBackButton />
      {viewMode === 'list' && (
        <div className="sticky top-[var(--lisad-header-h,88px)] z-30 mx-auto max-w-6xl px-3 sm:px-6" aria-label="Категории проектов">
          <div className="border-b border-site-border bg-site-bg/95 backdrop-blur-md">
            <div className="flex flex-wrap items-center gap-2 py-2">
              <div className="flex-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex gap-2 min-w-max">
                  {PROJECT_TABS.filter(tab => !tab.authOnly || isAuthed || tab.id === 'my').map((tab) => {
                    const disabled = Boolean(tab.authOnly && !isAuthed);
                    const active = activeTab === tab.id;
                    const count = tabCounts[tab.id];
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => setActiveTab(tab.id)}
                        aria-pressed={active}
                        className={`flex items-center gap-2 min-h-11 px-3 py-1.5 rounded-xl border text-sm transition-all ${
                          active
                            ? 'ui-selected border-site-selected-border'
                            : 'bg-site-surface/50 text-site-secondary border-site-border hover:text-site-text hover:border-site-border-control/70'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:-translate-y-[1px]'}`}
                      >
                        <UiIcon name={tab.icon} /><span>{tab.label}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-site-text/20 text-site-text' : 'bg-site-raised/70 text-site-text'}`}>
                          {count}
                        </span>
                        {(tab.id === 'invites' || tab.id === 'requests') && count > 0 ? (
                          <span className="w-2 h-2 rounded-full bg-site-warning animate-pulse" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 max-w-6xl mx-auto px-3 py-4 sm:px-6 sm:py-6">
        <div className="mb-5 flex items-center gap-2" aria-label="Поиск и вид каталога">
          <div className="relative min-w-0 flex-1">
            <input value={query} onChange={event => setQuery(event.target.value)} aria-label="Поиск по проектам"
              placeholder="Поиск по проектам…" className="w-full h-11 rounded-xl border border-site-border bg-site-surface/50 pl-3 pr-11 text-sm text-site-text placeholder:text-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="Очистить поиск" className="absolute right-0 top-0 grid h-11 w-11 place-items-center text-site-muted"><UiIcon name="X" /></button>}
          </div>
          <ViewModeToggle />
        </div>
        <label className="mb-5 block text-xs text-site-muted">
          Организация / подразделение
          <select value={organizationId ?? ''} onChange={event => selectOrganization(event.target.value)}
            className="mt-1 block h-11 w-full min-w-0 rounded-xl border border-site-border bg-site-surface px-3 text-sm text-site-text"
            disabled={!organizationCatalog || !!organizationCatalog.error}>
            <option value="">{!organizationCatalog ? 'Загружаем организации…' : organizationCatalog.error ? 'Организации недоступны' : 'Все организации'}</option>
            {organizationId !== null && !organizationCatalog?.data.some(item => Number(item.id) === organizationId) && <option value={organizationId}>{scopeName || `Организация ${organizationId}`}</option>}
            {organizationGroups.map(group => <optgroup key={group.label} label={group.label}>
              {group.items.map(item => <option key={item.id} value={item.id}>{item.short_name || item.name}{item.parent_short_name || item.parent_name ? ` — ${item.parent_short_name || item.parent_name}` : ''}</option>)}
            </optgroup>)}
          </select>
        </label>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl sm:text-4xl font-semibold text-site-text">
              Проекты{scopeName ? ` — ${scopeName}` : ''}
            </h1>
            <p className="hidden sm:block text-site-secondary mt-2">
              {viewMode === 'map' ? 'Навигация по структуре организаций' : 'Создавайте проекты, вступайте, собирайте команду'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAuthed && (
              <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                <UiIcon name="Plus" /> Создать
              </Button>
            )}
            <Button variant="outline" size="sm" className="hidden min-[761px]:inline-flex" onClick={refresh} disabled={loading}>
              {loading ? <><UiIcon name="Hourglass" />{" Обновление..."}</> : <><UiIcon name="ArrowsClockwise" />{" Обновить"}</>}
            </Button>
          </div>
        </div>

        {organizationId !== null && <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-site-border bg-site-surface/60 px-4 py-3 text-sm">
          <span role="status" className="text-site-secondary">{scopedOrganization?.error || (scopedOrganization ? 'Проекты организации и её подразделений' : 'Загружаем организацию…')}</span>
          <Link href="/projects" className="inline-flex min-h-11 items-center text-site-accent">Все организации <UiIcon name="ArrowUpRight" /></Link>
        </div>}

        {/* Режим "Карта" */}
        {viewMode === 'map' ? (
          <Card variant="glass" useWidgetSettings={true} className="p-6">
            <OrganizationMap projects={filteredAll} />
          </Card>
        ) : (
          <>
            {/* Режим "Список" - оригинальный контент */}
            {message && (
              <Card variant="glass" useWidgetSettings={true} className="mb-6">
                <div className={`p-4 rounded-lg text-center ${
                  message.type === 'success'
                    ? 'bg-site-success/20 text-site-success border border-site-success/50'
                    : 'bg-site-danger/20 text-site-danger border border-site-danger/50'
                }`}>
                  {message.text}
                </div>
              </Card>
            )}

            {showRequestsSection && (
              <Card variant="glass" useWidgetSettings={true} className="p-6 mb-8">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="text-xl font-semibold text-site-text">Заявки в мои проекты</h2>
                  <Badge variant="warning" size="sm">{filteredJoinRequests.length}</Badge>
                </div>
                {filteredJoinRequests.length ? (
                  <div className="space-y-3">
                    {filteredJoinRequests.map((r) => {
                      const s: any = (r as any).sender || {};
                      const title = (s.display_name || s.username || 'Пользователь') as string;
                      const createdAt = r.created_at ? new Date(r.created_at).toLocaleString('ru-RU') : '';
                      return (
                        <div key={r.id} className="rounded-xl border border-site-border bg-site-bg/20 p-4">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-site-text font-medium truncate">
                                {title} {s.username ? <span className="text-site-muted font-normal">@{(s as any)?.nickname || s.username}</span> : null}
                              </div>
                              <div className="text-sm text-site-secondary mt-1">
                                Проект: <span className="font-medium">{r.project_name}</span>
                              </div>
                              <div className="text-xs text-site-muted mt-1">{createdAt}</div>
                              {r.message ? <div className="mt-3 text-sm text-site-text whitespace-pre-wrap">Сообщение: {r.message}</div> : null}
                              <details className="mt-3 text-sm text-site-secondary">
                                <summary className="cursor-pointer text-site-muted hover:text-site-text transition-colors">Данные отправителя</summary>
                                <div className="mt-2 space-y-1">
                                  {s.email ? <div>Email: <span className="text-site-text">{String(s.email)}</span></div> : null}
                                  {s.phone ? <div>Телефон: <span className="text-site-text">{String(s.phone)}</span></div> : null}
                                  {(s.first_name || s.last_name || s.middle_name) && (
                                    <div>ФИО: <span className="text-site-text">{[s.last_name, s.first_name, s.middle_name].filter(Boolean).join(' ')}</span></div>
                                  )}
                                  {s.position ? <div>Должность: <span className="text-site-text">{String(s.position)}</span></div> : null}
                                  {s.organization ? <div>Организация: <span className="text-site-text">{String(s.organization)}</span></div> : null}
                                  {s.website ? <div>Сайт: <span className="text-site-text">{String(s.website)}</span></div> : null}
                                  {s.github_username ? <div>GitHub: <span className="text-site-text">{String(s.github_username)}</span></div> : null}
                                  {s.telegram_username ? <div>Telegram: <span className="text-site-text">{String(s.telegram_username)}</span></div> : null}
                                  {s.bio ? <div className="pt-2 text-site-muted whitespace-pre-wrap">{String(s.bio)}</div> : null}
                                </div>
                              </details>
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                              <Button
                                variant="primary"
                                size="sm"
                                loading={joinRequestsActing === r.id}
                                onClick={async () => {
                                  setJoinRequestsActing(r.id);
                                  try {
                                    await apiClient.acceptProjectJoinRequest(r.id);
                                    await refresh();
                                    setMessage({ type: 'success', text: 'Заявка принята' });
                                    setTimeout(() => setMessage(null), 1200);
                                  } catch (e: any) {
                                    setMessage({ type: 'error', text: e?.message || 'Ошибка принятия заявки' });
                                  } finally {
                                    setJoinRequestsActing(null);
                                  }
                                }}
                              >
                                <UiIcon name="CheckCircle" /> Принять
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                loading={joinRequestsActing === r.id}
                                onClick={async () => {
                                  setJoinRequestsActing(r.id);
                                  try {
                                    await apiClient.declineProjectJoinRequest(r.id);
                                    await refresh();
                                    setMessage({ type: 'success', text: 'Заявка отклонена' });
                                    setTimeout(() => setMessage(null), 1200);
                                  } catch (e: any) {
                                    setMessage({ type: 'error', text: e?.message || 'Ошибка отклонения заявки' });
                                  } finally {
                                    setJoinRequestsActing(null);
                                  }
                                }}
                              >
                                <UiIcon name="X" /> Отклонить
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-site-muted">Заявок пока нет.</p>
                )}
              </Card>
            )}

            {showInvitesSection && (
              <Card variant="glass" useWidgetSettings={true} className="p-6 mb-8">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="text-xl font-semibold text-site-text">Приглашения в проекты</h2>
                  <Badge variant="warning" size="sm">{filteredInvites.length}</Badge>
                </div>
                {filteredInvites.length ? (
                  <div className="space-y-3">
                    {filteredInvites.map((inv) => (
                      <div key={inv.id} className="rounded-xl border border-site-border bg-site-bg/20 p-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <p className="text-site-text font-medium">{inv.project_name}</p>
                            <p className="text-sm text-site-muted mt-1">Пригласил: {inv.invited_by_display || inv.invited_by_username}</p>
                            {inv.message && <p className="text-sm text-site-secondary mt-2 whitespace-pre-wrap">{inv.message}</p>}
                          </div>
                          <div className="flex gap-2">
                            <Button variant="primary" size="sm" onClick={() => acceptInvite(inv.id)}><UiIcon name="CheckCircle" /> Принять</Button>
                            <Button variant="outline" size="sm" onClick={() => declineInvite(inv.id)}><UiIcon name="X" /> Отклонить</Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-site-muted">Приглашений пока нет.</p>
                )}
              </Card>
            )}

            {/* Модальное окно создания проекта */}
            {showCreateModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
                <Card variant="glass" useWidgetSettings={true} className="relative z-10 w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto p-6">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h2 className="text-xl font-semibold text-site-text">Создать проект</h2>
                    <button onClick={() => setShowCreateModal(false)} className="text-site-muted hover:text-site-text transition-colors">✕</button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-site-secondary mb-2">Название</label>
                      <input
                        value={createForm.name}
                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value.slice(0, 40) })}
                        className="w-full px-3 py-2 bg-site-surface/60 border border-site-border-control/50 rounded-lg focus:ring-2 focus:ring-site-accent focus:border-site-accent text-site-text placeholder-site-muted transition-all duration-200"
                        placeholder="Например: Анализ данных КБГУ"
                        maxLength={40}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-site-secondary mb-2">Описание</label>
                      <textarea
                        value={createForm.description}
                        onChange={(e) => setCreateForm({ ...createForm, description: e.target.value.slice(0, 200) })}
                        className="w-full px-3 py-2 bg-site-surface/60 border border-site-border-control/50 rounded-lg focus:ring-2 focus:ring-site-accent focus:border-site-accent text-site-text placeholder-site-muted transition-all duration-200 resize-none"
                        placeholder="Коротко: цель, направление, стек..."
                        maxLength={200}
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-site-secondary mb-2">
                        Организация <span className="text-site-danger">*</span>
                      </label>
                      <OrganizationSelect
                        value={createForm.organization_id}
                        onChange={(orgId) => setCreateForm({ ...createForm, organization_id: orgId })}
                        placeholder="Выберите лабораторию, кафедру..."
                        filterType={['laboratory', 'department', 'school', 'gymnasium', 'organization']}
                        showClear={false}
                      />
                      <p className="text-xs text-site-muted mt-1">
                        Можно выбрать: лабораторию, кафедру, школу, гимназию или организацию
                      </p>
                    </div>
                    <ProjectDirectionPicker
                      value={createForm.directions}
                      onChange={(directions) => setCreateForm((prev) => ({ ...prev, directions }))}
                      disabled={creating}
                    />
                    <label className="flex items-center gap-2 text-sm text-site-secondary">
                      <input
                        type="checkbox"
                        checked={createForm.is_public}
                        onChange={(e) => setCreateForm({ ...createForm, is_public: e.target.checked })}
                      />
                      Публичный проект (виден всем)
                    </label>
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <Button variant="outline" size="sm" onClick={() => setShowCreateModal(false)}>Отмена</Button>
                    <Button variant="primary" size="sm" loading={creating} onClick={async () => {
                      await createProject();
                      if (!message?.type || message.type === 'success') setShowCreateModal(false);
                    }}><UiIcon name="Plus" /> Создать</Button>
                  </div>
                </Card>
              </div>
            )}

            {showProjectsGrid && (
              <>
                {/* Вкладка "Все" - все проекты в одном списке */}
                {showAll && (
                  <Card variant="glass" useWidgetSettings={true} className="p-6">
                    <h2 className="text-xl font-semibold text-site-text mb-4">Все проекты</h2>
                    {filteredAll.length ? (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredAll.map((p) => (
                          <div key={p.id} className="rounded-xl border border-site-border bg-site-bg/20 p-4 hover:border-site-border-control/60 transition-all">
                            <div className="flex flex-col h-full">
                              <Link href={`/projects/${p.id}`} className="text-site-text font-medium hover:text-site-accent transition-colors">
                                {p.name}
                              </Link>
                              {p.description && <p className="text-sm text-site-secondary mt-1 line-clamp-2">{p.description}</p>}
                              <div className="flex flex-wrap gap-2 mt-3">
                                <Badge variant={statusVariant(p.status)} size="sm">{statusLabel(p.status)}</Badge>
                                {myIds.has(p.id) && <Badge variant="success" size="sm">Участник</Badge>}
                              </div>
                              <div className="mt-auto pt-3">
                                <div className="flex justify-between text-xs text-site-muted mb-1">
                                  <span>Прогресс</span>
                                  <span className="text-site-secondary">{Math.max(0, Math.min(100, p.progress ?? 0))}%</span>
                                </div>
                                <div className="h-1.5 bg-site-surface/60 rounded-full overflow-hidden border border-site-border">
                                  <div className="h-full bg-site-progress" style={{ width: `${Math.max(0, Math.min(100, p.progress ?? 0))}%` }} />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-site-muted">Публичных проектов пока нет.</p>
                    )}
                  </Card>
                )}

                {/* Остальные вкладки в grid */}
                {(showMy || showLab || showCommunity) && (
                  <div className="grid lg:grid-cols-2 gap-6">
                    {isAuthed && showMy && (
                      <Card variant="glass" useWidgetSettings={true} className="p-6">
                        <h2 className="text-xl font-semibold text-site-text mb-4">Мои проекты</h2>
                        {filteredMy.length ? (
                          <div className="space-y-3">
                            {filteredMy.map((p) => (
                              <div key={p.id} className="rounded-xl border border-site-border bg-site-bg/20 p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <Link href={`/projects/${p.id}`} className="text-site-text font-medium hover:text-site-accent transition-colors">
                                      {p.name}
                                    </Link>
                                    {p.description && <p className="text-sm text-site-secondary mt-1">{p.description}</p>}
                                    <div className="flex flex-wrap gap-2 mt-3">
                                      <Badge variant="secondary" size="sm">{p.my_role || 'member'}</Badge>
                                      <Badge variant="secondary" size="sm" className="gap-1"><IconUser className="w-3.5 h-3.5" /> {p.members_count ?? 0}</Badge>
                                      <Badge variant={statusVariant(p.status)} size="sm">{statusLabel(p.status)}</Badge>
                                      {p.is_public ? <Badge variant="success" size="sm">public</Badge> : <Badge variant="warning" size="sm">private</Badge>}
                                    </div>
                                    <div className="mt-3">
                                      <div className="flex justify-between text-xs text-site-muted mb-1">
                                        <span>Уровень реализации</span>
                                        <span className="text-site-secondary">{Math.max(0, Math.min(100, p.progress ?? 0))}%</span>
                                      </div>
                                      <div className="h-2 bg-site-surface/60 rounded-full overflow-hidden border border-site-border">
                                        <div className="h-full bg-site-progress" style={{ width: `${Math.max(0, Math.min(100, p.progress ?? 0))}%` }} />
                                      </div>
                                    </div>
                                  </div>
                                  {p.my_role === 'owner' ? (
                                    <Badge variant="custom" size="sm" color="#F59E0B">Владелец</Badge>
                                  ) : (
                                    <Button variant="outline" size="sm" onClick={() => leave(p.id)}>Выйти</Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-site-muted">Пока нет. Создайте проект или вступите в публичный.</p>
                        )}
                      </Card>
                    )}

                    {showLab && (
                      <Card variant="glass" useWidgetSettings={true} className="p-6">
                        <h2 className="text-xl font-semibold text-site-text mb-4">Проекты лаборатории</h2>
                        {filteredPublic.length ? (
                          <div className="space-y-3">
                            {filteredPublic.map((p) => (
                              <div key={p.id} className="rounded-xl border border-site-border bg-site-bg/20 p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <Link href={`/projects/${p.id}`} className="text-site-text font-medium hover:text-site-accent transition-colors">{p.name}</Link>
                                    {p.description && <p className="text-sm text-site-secondary mt-1">{p.description}</p>}
                                    <div className="flex flex-wrap gap-2 mt-3">
                                      <Badge variant={statusVariant(p.status)} size="sm">{statusLabel(p.status)}</Badge>
                                      <Badge variant="success" size="sm">public</Badge>
                                    </div>
                                    <div className="mt-3">
                                      <div className="flex justify-between text-xs text-site-muted mb-1">
                                        <span>Уровень реализации</span>
                                        <span className="text-site-secondary">{Math.max(0, Math.min(100, p.progress ?? 0))}%</span>
                                      </div>
                                      <div className="h-2 bg-site-surface/60 rounded-full overflow-hidden border border-site-border">
                                        <div className="h-full bg-site-progress" style={{ width: `${Math.max(0, Math.min(100, p.progress ?? 0))}%` }} />
                                      </div>
                                    </div>
                                  </div>
                                  {myIds.has(p.id) ? (
                                    <Badge variant="success" size="sm">Вы участник</Badge>
                                  ) : isAuthed ? (() => {
                                    const jr = myJoinByProject.get(p.id);
                                    if (jr?.status === 'pending') return <Button variant="outline" size="sm" disabled>Заявка отправлена</Button>;
                                    return <Button variant="primary" size="sm" onClick={() => join(p.id)}>{jr?.status === 'declined' ? 'Подать снова' : 'Подать заявку'}</Button>;
                                  })() : (
                                    <Button variant="outline" size="sm"><Link href="/login">Войти</Link></Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-site-muted">Публичных проектов лаборатории пока нет.</p>
                        )}
                      </Card>
                    )}

                    {showCommunity && (
                      <Card variant="glass" useWidgetSettings={true} className="p-6">
                        <h2 className="text-xl font-semibold text-site-text mb-4">Публичные проекты пользователей</h2>
                        {filteredPublicUsers.length ? (
                          <div className="space-y-3">
                            {filteredPublicUsers.map((p) => (
                              <div key={p.id} className="rounded-xl border border-site-border bg-site-bg/20 p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <Link href={`/projects/${p.id}`} className="text-site-text font-medium hover:text-site-accent transition-colors">{p.name}</Link>
                                    {p.description && <p className="text-sm text-site-secondary mt-1">{p.description}</p>}
                                    <div className="flex flex-wrap gap-2 mt-3">
                                      <Badge variant={statusVariant(p.status)} size="sm">{statusLabel(p.status)}</Badge>
                                      <Badge variant="success" size="sm">public</Badge>
                                    </div>
                                    <div className="mt-3">
                                      <div className="flex justify-between text-xs text-site-muted mb-1">
                                        <span>Уровень реализации</span>
                                        <span className="text-site-secondary">{Math.max(0, Math.min(100, p.progress ?? 0))}%</span>
                                      </div>
                                      <div className="h-2 bg-site-surface/60 rounded-full overflow-hidden border border-site-border">
                                        <div className="h-full bg-site-progress" style={{ width: `${Math.max(0, Math.min(100, p.progress ?? 0))}%` }} />
                                      </div>
                                    </div>
                                  </div>
                                  {myIds.has(p.id) ? (
                                    <Badge variant="success" size="sm">Вы участник</Badge>
                                  ) : isAuthed ? (() => {
                                    const jr = myJoinByProject.get(p.id);
                                    if (jr?.status === 'pending') return <Button variant="outline" size="sm" disabled>Заявка отправлена</Button>;
                                    return <Button variant="primary" size="sm" onClick={() => join(p.id)}>{jr?.status === 'declined' ? 'Подать снова' : 'Подать заявку'}</Button>;
                                  })() : (
                                    <Button variant="outline" size="sm"><Link href="/login">Войти</Link></Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-site-muted">Публичных проектов пользователей пока нет.</p>
                        )}
                      </Card>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </ProjectPullRefresh>
  );
}
